import { useCallback, useMemo, useRef, useState } from "react";
import {
  OperationProgressItem,
  OperationProgressStatus,
} from "./AsyncOperationProgress";

export interface UseAsyncOperationProgressState {
  status: OperationProgressStatus;
  currentItemLabel: string;
  processedCount: number;
  totalCount: number;
  items: OperationProgressItem[];
}

export interface UseAsyncOperationProgressActions {
  /** Initialize progress tracking with items. Must be called before starting operation */
  initialize: (items: OperationProgressItem[]) => void;
  /** Update current item being processed */
  setCurrentItem: (label: string) => void;
  /** Mark an item as processed (success or failure) */
  markItemProcessed: (itemId: string, success: boolean, error?: Error) => void;
  /** Start processing (transitions from IDLE to PROCESSING) */
  startProcessing: () => void;
  /** Cancel ongoing processing */
  cancel: () => void;
  /** Complete the operation */
  complete: () => void;
  /** Reset to idle state */
  reset: () => void;
  /** Is in processing or cancelling status */
  isBusy: () => boolean;
  /** Is cancelling? */
  isCancelling: () => boolean;
}

export const useAsyncOperationProgress = (): UseAsyncOperationProgressState &
  UseAsyncOperationProgressActions => {
  const [state, setState] = useState<UseAsyncOperationProgressState>({
    status: OperationProgressStatus.IDLE,
    currentItemLabel: "",
    processedCount: 0,
    totalCount: 0,
    items: [],
  });

  const cancelRef = useRef(false);

  const initialize = useCallback((items: OperationProgressItem[]) => {
    if (items.length === 0) {
      console.warn(
        "You just added empty items array, this might be not what you've intended",
      );
    }
    setState((prev) => ({
      ...prev,
      items: items.map((item) => ({
        ...item,
        success: false,
      })),
      totalCount: items.length,
      processedCount: 0,
      currentItemLabel: "",
    }));
  }, []);

  const setCurrentItem = useCallback((label: string) => {
    setState((prev) => ({
      ...prev,
      currentItemLabel: label,
    }));
  }, []);

  const markItemProcessed = useCallback(
    (itemId: string, success: boolean, error?: Error) => {
      setState((prev) => {
        const newItems = [...prev.items];
        const itemIndex = newItems.findIndex((item) => item.id === itemId);
        if (itemIndex === -1) {
          return prev;
        }
        newItems[itemIndex] = {
          ...newItems[itemIndex],
          success,
          errorDescription: error ? error.message : undefined,
        };
        return {
          ...prev,
          items: newItems,
          processedCount: prev.processedCount + 1,
        };
      });
    },
    [],
  );

  const startProcessing = useCallback(() => {
    cancelRef.current = false;
    setState((prev) => {
      if (prev.items.length === 0) {
        console.warn(
          "You are processing empty items array, this might be not what you've intended",
          prev,
        );
        return prev;
      }
      return {
        ...prev,
        status: OperationProgressStatus.PROCESSING,
      };
    });
  }, []);

  const cancel = useCallback(() => {
    cancelRef.current = true;
    setState((prev) => {
      if (prev.items.length === 0) {
        console.warn(
          "You are processing empty items array, this might be not what you've intended",
        );
        return prev;
      }
      return {
        ...prev,
        status: OperationProgressStatus.CANCELLING,
      };
    });
  }, []);

  const complete = useCallback(() => {
    setState((prev) => {
      if (prev.items.length === 0) {
        console.warn(
          "You are processing empty items array, this might be not what you've intended",
          prev,
        );
        return prev;
      }
      return {
        ...prev,
        status:
          prev.status === OperationProgressStatus.CANCELLING
            ? OperationProgressStatus.CANCELLED
            : OperationProgressStatus.COMPLETED,
      };
    });
  }, []);

  const reset = useCallback(() => {
    setState((prev) => {
      if (prev.status === OperationProgressStatus.IDLE) {
        return prev;
      }
      return {
        status: OperationProgressStatus.IDLE,
        currentItemLabel: "",
        processedCount: 0,
        totalCount: 0,
        items: [],
      };
    });
    cancelRef.current = false;
  }, []);

  const isBusy = useCallback(
    () =>
      state.status === OperationProgressStatus.PROCESSING ||
      state.status === OperationProgressStatus.CANCELLING,
    [state],
  );

  const isCancelling = () => cancelRef.current;

  return useMemo(
    () => ({
      ...state,
      initialize,
      setCurrentItem,
      markItemProcessed,
      startProcessing,
      cancel,
      complete,
      reset,
      isBusy,
      isCancelling,
    }),
    [state],
  );
};
